import {
	Center,
	createStyles,
	Group,
	Image,
	Stack,
	TextInput,
} from '@mantine/core';
import { ChromeMessageType } from '@repo/common/constants';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Button, Icon, Text } from '@repo/foundations';
import axios from 'axios';
import { isNil } from 'lodash-es';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useAuthUser } from '../../api';
import { createApiKey } from '../../api/hooks/apiKey';
import { REGION_REDIRECT_URL } from '../../api/hooks/useRegionRedirect';
import { anonymousId } from '../../components/Auth/AuthStepEmailComponent.constants';
import { CHROME_EXTENSION_ID_LOCAL_STORAGE_KEY } from '../../components/Settings/ChromeExtensionSettings/constants';
import { useUserOnboarding } from '../../hooks/useUserOnboarding';
import { IS_LOCAL_OR_DEVELOPMENT } from '../../utils/envs';
import { sendChromeMessage } from '../../utils/extension';

const useStyles = createStyles(() => ({
	wrapper: {
		width: '100%',
		height: '100vh',
	},
}));

function ChromeExtensionPage() {
	const { classes } = useStyles();

	const [extensionId, setExtensionId] = useState('');

	const navigate = useNavigate();
	const { dismissViewerOnboardingStepChromeExtension } = useUserOnboarding();

	const { domain } = useAuthUser();

	const handleGetSecodaRedirectURL = useCallback(async () => {
		const response = await axios.get(REGION_REDIRECT_URL, {
			params: { domain },
		});
		return response.data.to;
	}, [domain]);

	const handleAuthenticate = async () => {
		const response = await createApiKey({
			data: {
				key: '',
				is_chrome_extension_key: true, // indicates that the key is used by the Chrome Extension
			},
		});

		if (!isNil(response.key)) {
			const redirectURL = await handleGetSecodaRedirectURL();

			sendChromeMessage({
				type: ChromeMessageType.CONNECT,
				token: response.key,
				anonymousId: anonymousId(),
				url: window.location.origin,
				redirectURL,
			});
			// Set chrome extension step as completed under user
			dismissViewerOnboardingStepChromeExtension();
			navigate('/');
		}
	};

	const handleExtensionIdChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		localStorage.setItem(
			CHROME_EXTENSION_ID_LOCAL_STORAGE_KEY,
			event.currentTarget.value
		);
		setExtensionId(event.currentTarget.value);
	};

	return (
		<Center className={classes.wrapper}>
			<Stack spacing="lg" w={480} align="center">
				<Group spacing="xs">
					<Image
						src="/images/logos/cfsecoda.webp"
						width={36}
						height={36}
						radius="sm"
					/>
					<Icon name="arrowsRightLeft" />
					<Image src="/images/logos/chrome.svg" width={36} height={36} />
				</Group>
				<Stack spacing="sm">
					<Text
						size="xl"
						weight="bold"
						lh="28px"
						color="text/primary/default"
						align="center"
					>
						Authenticate Chrome Extension
					</Text>
					<Text
						size="md"
						weight="regular"
						lh="24px"
						color="text/secondary/default"
						align="center"
					>
						Access metadata, ask AI-powered questions, and search your company
						data from anywhere in Chrome.
					</Text>
				</Stack>
				<Stack spacing="sm" w={200}>
					{IS_LOCAL_OR_DEVELOPMENT && (
						<TextInput
							value={extensionId}
							onChange={handleExtensionIdChange}
							placeholder="Enter Chrome Extension ID"
						/>
					)}
					<Button size="lg" onClick={handleAuthenticate}>
						Authenticate
					</Button>
				</Stack>
			</Stack>
		</Center>
	);
}

export default ChromeExtensionPage;
